import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error,
  SignalCellularAlt,
  Storage as Database,
  Memory,
  AccessTime,
  Battery20,
  Battery50,
  Battery80,
  BatteryFull,
  Thermostat,
} from '@mui/icons-material';

interface SystemHealthData {
  overall?: string;
  services?: {
    database: boolean;
    redis: boolean;
    gpsProcessing: boolean;
  };
  trackerHealth?: {
    online: number;
    offline: number;
    totalTrackers: number;
  };
  performance?: {
    memoryUsage: number;
    cpuUsage: number;
    responseTime: number;
  };
  alerts?: Array<{
    level: 'info' | 'warning' | 'error';
    message: string;
    timestamp: string;
  }>;
  lastDataUpdate?: string;
}

interface TrackerData {
  trackerId: string;
  customTitle: string;
  status: 'active' | 'standby' | 'offline';
  signalBars?: number;
  signalStrength?: number;
  networkType?: string;
  battery?: number;
  batteryLevel?: number;
  temperature?: number;
  lastUpdate: string;
}

interface SystemHealthProps {
  systemHealth?: SystemHealthData;
  trackers: TrackerData[];
}

// Utility function to format time ago (like Facebook posts)
const formatTimeAgo = (timestamp: string | Date): string => {
  const now = new Date();
  const lastUpdate = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - lastUpdate.getTime()) / 1000);

  if (diffInSeconds < 5) return 'just now';
  if (diffInSeconds < 60) return `${diffInSeconds}s ago`;

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
};

// Get color based on data freshness
const getDataFreshnessColor = (timestamp: string | Date): string => {
  const now = new Date();
  const lastUpdate = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - lastUpdate.getTime()) / 1000);

  if (diffInSeconds < 30) return '#4caf50'; // Green - very fresh
  if (diffInSeconds < 60) return '#ff9800'; // Orange - getting old
  return '#f44336'; // Red - stale data
};

// Utility function to determine tracker status based on data freshness
const getTrackerStatus = (lastUpdate: string | Date): 'active' | 'standby' | 'offline' => {
  const now = new Date();
  const lastUpdateTime = new Date(lastUpdate);
  const diffInMinutes = Math.floor((now.getTime() - lastUpdateTime.getTime()) / (1000 * 60));

  // If more than 30 minutes without data, consider offline
  if (diffInMinutes >= 30) {
    return 'offline';
  }

  // If more than 5 minutes without data, consider standby
  if (diffInMinutes >= 5) {
    return 'standby';
  }

  // Recent data, keep as active
  return 'active';
};

const SystemHealth: React.FC<SystemHealthProps> = ({ systemHealth, trackers }) => {
  // Force re-render every second to update real-time status badges
  const [, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: boolean | undefined) => {
    if (status === true) return <CheckCircle color="success" />;
    if (status === false) return <Error color="error" />;
    return <Warning color="warning" />;
  };

  const getStatusColor = (status: boolean | undefined) => {
    if (status === true) return 'success';
    if (status === false) return 'error';
    return 'warning';
  };

  const getPerformanceColor = (value: number, type: 'memory' | 'cpu' | 'response') => {
    switch (type) {
      case 'memory':
      case 'cpu':
        if (value < 50) return 'success';
        if (value < 80) return 'warning';
        return 'error';
      case 'response':
        if (value < 100) return 'success';
        if (value < 500) return 'warning';
        return 'error';
      default:
        return 'primary';
    }
  };

  const getSignalQuality = (bars?: number) => {
    if (bars === undefined || bars < 2) return { color: 'error', text: 'Poor' };
    if (bars < 4) return { color: 'warning', text: 'Fair' };
    return { color: 'success', text: 'Good' };
  };


  // Calculate overall system status
  const overallStatus = () => {
    const services = systemHealth?.services;

    if (services?.gpsProcessing && services?.database) {
      return { status: 'healthy', color: 'success', text: 'System Operational' };
    }

    if (services?.database) {
      return { status: 'degraded', color: 'warning', text: 'Partial Service' };
    }

    return { status: 'critical', color: 'error', text: 'System Issues' };
  };

  const systemStatus = overallStatus();

  return (
    <Box>
      {/* Overall System Status */}
      <Card sx={{ mb: 2 }} variant="outlined">
        <CardContent sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            {getStatusIcon(systemStatus.status === 'healthy')}
            <Box>
              <Typography variant="h6">{systemStatus.text}</Typography>
              <Typography variant="caption" color="text.secondary">
                Last checked: {new Date().toLocaleTimeString()}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* System Services */}
      <Card sx={{ mb: 2 }} variant="outlined">
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            System Services
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                {getStatusIcon(systemHealth?.services?.database)}
              </ListItemIcon>
              <ListItemText
                primary="Database Server"
                secondary={systemHealth?.services?.database ? 'MongoDB Connected' : 'Database Offline'}
              />
              <Chip
                label={systemHealth?.services?.database ? 'OK' : 'ERROR'}
                size="small"
                color={getStatusColor(systemHealth?.services?.database) as any}
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                {getStatusIcon(systemHealth?.services?.gpsProcessing)}
              </ListItemIcon>
              <ListItemText
                primary="GPS Processing"
                secondary={systemHealth?.services?.gpsProcessing ? 'Active and processing data' : 'Not processing'}
              />
              <Chip
                label={systemHealth?.services?.gpsProcessing ? 'OK' : 'ERROR'}
                size="small"
                color={getStatusColor(systemHealth?.services?.gpsProcessing) as any}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Database Status */}
      <Card sx={{ mb: 2 }} variant="outlined">
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Database Connections
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Database color={getStatusColor(systemHealth?.services?.database)} />
                <Box>
                  <Typography variant="body2">MongoDB</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {systemHealth?.services?.database ? 'Connected' : 'Disconnected'}
                  </Typography>
                </Box>
              </Stack>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Memory color={getStatusColor(systemHealth?.services?.redis)} />
                <Box>
                  <Typography variant="body2">Redis</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {systemHealth?.services?.redis ? 'Connected' : 'Disconnected'}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      {systemHealth?.performance && (
        <Card sx={{ mb: 2 }} variant="outlined">
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Performance Metrics
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Memory Usage</Typography>
                  <Typography variant="body2">
                    {systemHealth.performance.memoryUsage.toFixed(1)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={systemHealth.performance.memoryUsage}
                  color={getPerformanceColor(systemHealth.performance.memoryUsage, 'memory') as any}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>

              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">CPU Usage</Typography>
                  <Typography variant="body2">
                    {systemHealth.performance.cpuUsage.toFixed(1)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={systemHealth.performance.cpuUsage}
                  color={getPerformanceColor(systemHealth.performance.cpuUsage, 'cpu') as any}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>

              <Stack direction="row" alignItems="center" spacing={1}>
                <AccessTime color={getPerformanceColor(systemHealth.performance.responseTime, 'response')} />
                <Typography variant="body2">
                  Response Time: {systemHealth.performance.responseTime}ms
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Tracker Status Summary */}
      <Card sx={{ mb: 2 }} variant="outlined">
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Tracker Status
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            {['active', 'standby', 'offline'].map((status) => {
              // Compute real-time status based on lastUpdate
              const count = trackers.filter(t => {
                const computedStatus = t.lastUpdate ? getTrackerStatus(t.lastUpdate) : 'offline';
                return computedStatus === status;
              }).length;
              return (
                <Chip
                  key={status}
                  label={`${count} ${status}`}
                  size="medium"
                  color={
                    status === 'active' ? 'success' :
                    status === 'standby' ? 'warning' : 'error'
                  }
                  sx={{
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    px: 1
                  }}
                />
              );
            })}
          </Box>

          {/* Device Status Summary */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Device Status
            </Typography>
            <Stack spacing={1}>
              {trackers.map((tracker) => {
                const signalQuality = getSignalQuality(tracker.signalStrength);
                return (
                  <Stack key={tracker.trackerId} direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption">
                      {tracker.customTitle || tracker.trackerId}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <SignalCellularAlt
                        fontSize="small"
                        color={signalQuality.color as any}
                      />
                      <Typography variant="caption">
                        {tracker.signalStrength !== undefined ? `${tracker.signalStrength}/5` : 'N/A'}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {tracker.batteryLevel !== undefined ? (
                          <>
                            {tracker.batteryLevel > 80 ? <BatteryFull color="success" fontSize="small" /> :
                             tracker.batteryLevel > 50 ? <Battery80 color="warning" fontSize="small" /> :
                             tracker.batteryLevel > 20 ? <Battery50 color="warning" fontSize="small" /> :
                             <Battery20 color="error" fontSize="small" />}
                            <Typography variant="caption">
                              {tracker.batteryLevel}%
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="caption">N/A</Typography>
                        )}
                      </Stack>
                      {tracker.temperature !== undefined && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Thermostat
                            fontSize="small"
                            color={tracker.temperature > 60 ? "error" : tracker.temperature > 40 ? "warning" : "primary"}
                          />
                          <Typography variant="caption">
                            {tracker.temperature}°C
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* System Alerts */}
      {systemHealth?.alerts && systemHealth.alerts.length > 0 && (
        <Card variant="outlined">
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Recent Alerts
            </Typography>
            <Stack spacing={1}>
              {systemHealth.alerts.slice(0, 3).map((alert, index) => (
                <Alert key={index} severity={alert.level}>
                  <Typography variant="caption">
                    {alert.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {new Date(alert.timestamp).toLocaleTimeString()}
                  </Typography>
                </Alert>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SystemHealth;